import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import API_BASE_URL from '../../config';
import './OrgSettings.css';
import loadingGif from '../../assets/loading.gif';
import { ToastContainer, toast } from 'react-toastify';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { IconButton } from '@mui/material';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Grid,
  Box,
  Divider,
  CardActions
} from '@mui/material';

const OrgSettings = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [orgDetails, setOrgDetails] = useState({
    orgName: '',
    orgEmail: '',
    orgNumber: '',
    isEnabled: false,
    orderPrefixes: []
  });
  const [newPrefix, setNewPrefix] = useState('');
  const [prefixesToAdd, setPrefixesToAdd] = useState([]);
  const [prefixesToDelete, setPrefixesToDelete] = useState([]);
  const [error, setError] = useState(null);
  const [tags, setTags] = useState([
    { tagIndex: 'tag1', tagName: 'tag1' },
    { tagIndex: 'tag2', tagName: 'tag2' },
    { tagIndex: 'tag3', tagName: 'tag3' },
    { tagIndex: 'tag4', tagName: 'tag4' }
  ]);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-details`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId: orgId },
        });

        if (response.data) {
          setOrgDetails(response.data);
        }

        // Fetch tags
        const tagsResponse = await axios.get(`${API_BASE_URL}/orders/order-tags`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
        });
  
        if (tagsResponse.data && Array.isArray(tagsResponse.data)) {
          setTags(tagsResponse.data);
        }

      } catch (error) {
        console.error(`Error fetching details for org ${orgId}:`, error);
        
        if (error.response && (error.response.status === 403 || error.response.status === 404)) {
          navigate('/unauthorized');
        } else {
          setError('Failed to fetch organization details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationDetails();
  }, [orgId, token, navigate]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
      }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      isEnabled: e.target.checked
    }));
  };

  const handleTagChange = (index, value) => {
    setTags(prevTags => {
      const newTags = [...prevTags];
      newTags[index] = { ...newTags[index], tagName: value };
      return newTags;
    });
  };  
  

  const handleAddPrefix = () => {
    const trimmedPrefix = newPrefix.trim().toUpperCase(); // Convert to uppercase
    if (trimmedPrefix === '' || orgDetails.orderPrefixes.includes(trimmedPrefix)) return; // Prevent adding empty or duplicate prefixes
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      orderPrefixes: [...prevDetails.orderPrefixes, trimmedPrefix], // Add the new prefix
    }));
    setPrefixesToAdd((prev) => [...prev, trimmedPrefix]); // Track the prefixes to add
    setNewPrefix(''); // Clear the input field
  };

  const handleDeletePrefix = (index) => {
    const prefixToDelete = orgDetails.orderPrefixes[index];
    setOrgDetails((prevDetails) => ({
      ...prevDetails,
      orderPrefixes: prevDetails.orderPrefixes.filter((_, i) => i !== index), // Remove the prefix at the specified index
    }));
    setPrefixesToDelete((prev) => [...prev, prefixToDelete]); // Track the prefixes to delete
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/org/organization-settings/organization-details/update`, {
        ...orgDetails,
        prefixesToAdd,
        prefixesToDelete
      }, {
        headers: { Authorization: `Bearer ${token}` },
        params: { orgId: orgId }
      });

      // Update tags
      await axios.post(`${API_BASE_URL}/org/organization-settings/update-tags`, {
        orgId: orgId,
        tags: tags
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Show the success alert
      const result = await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.data.message || 'Organization details updated successfully.',
      });

      // Redirect if the user clicks "OK"
      if (result.isConfirmed) {
        navigate('/organization/settings');
      }

    } catch (error) {
      console.error('Error updating organization details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to update organization details.',
      });
    }
  };

  const handlePrefixChange = (e) => {
    const value = e.target.value;
    // Allow only alphanumeric, max 6 chars
    if (/^[a-zA-Z0-9]{0,6}$/.test(value)) {
      setNewPrefix(value);
    }
  };
  if (error) {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: error,
    });
    return null;
  }

  if (!orgDetails) {
    return <div>Loading organization details...</div>;
  }

  const maxPrefixes = 6; // Maximum number of prefixes
  const prefixesCount = orgDetails.orderPrefixes.length;

  return (
    <Card sx={{ width: '100%', margin: 'auto', mt: 0, background: 'none', boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
        <IconButton onClick={() => navigate('/organization/settings')} sx={{ mr: 2 }}>
        <KeyboardReturnRoundedIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Edit Organization Details
        </Typography>
      </Box>
      <Divider />  
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Organization Name"
                name="orgName"
                value={orgDetails.orgName}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={1}
                size="small"
                margin="normal"
              />
              <TextField
                label="Organization Email"
                name="orgEmail"
                value={orgDetails.orgEmail}
                onChange={handleInputChange}
                multiline
                rows={1}
                fullWidth
                size="small"
                margin="normal"
              />
              <TextField
                label="Organization Phone Number"
                name="orgNumber"
                value={orgDetails.orgNumber}
                onChange={handleInputChange}
                multiline
                rows={1}
                fullWidth
                size="small"
                margin="normal"
              />
              <FormControlLabel
                control={<Switch checked={orgDetails.isEnabled} onChange={handleSwitchChange} color="primary" />}
                label="Organization Enabled"
              />
            </Grid>
            <Divider />
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Order Prefixes</Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <TextField
                  placeholder="Add new prefix"
                  value={newPrefix}
                  onChange={handlePrefixChange}
                  inputProps={{ maxLength: 6 }}
                  size="small"
                  multiline
                  rows={1}
                  style={{ marginRight: 8 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddPrefix}
                  disabled={orgDetails.orderPrefixes.length >= maxPrefixes || !newPrefix}
                >Add</Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {orgDetails.orderPrefixes.map((prefix, idx) => (
                  <Chip
                    key={prefix}
                    label={prefix}
                    onDelete={() => handleDeletePrefix(idx)}
                    color="secondary"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="textSecondary">
                *Max {maxPrefixes} prefixes allowed. Prefixes must be 1-6 alphanumeric characters.
              </Typography>
            </Grid>
          </Grid>
          <Divider style={{ margin: '24px 0' }} />
          <Typography variant="subtitle1" gutterBottom>Order Tags</Typography>
          <Grid container spacing={2}>
            {tags.map((tag, idx) => (
              <Grid item xs={12} sm={6} md={3} key={tag.tagIndex}>
                <TextField
                  label={`Tag ${idx + 1}`}
                  value={tag.tagName}
                  onChange={e => handleTagChange(idx, e.target.value)}
                  fullWidth
                  multiline
                  rows={1}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
        <CardActions style={{ justifyContent: 'flex-end' }}>
          <Button variant="contained" color="primary" type="submit" onClick={handleSubmit}>
            Save Changes
          </Button>
        </CardActions>
      </Card>
    
  );
};

export default OrgSettings;
